
import { message } from 'ant-design-vue';
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { SearchIndexItem } from '../types';

export default defineComponent({
  props: {
    searchIndex: { type: Array as PropType<SearchIndexItem[]>, required: true },
    isLoadingCourses: Boolean,
  },
  emits: ['hide-search-dialog', 'add-course'],
  setup(props) {
    /** 搜索表单字串 */
    const searchQuery = ref({
      name: '',
      teachers: '',
      department: '',
      day: '全部',
      sectionRange: [0, 13] as [number, number],
      place: '',
      codeId: '',
    });

    /** 节次滑条标签数组 */
    const sectionLabels = computed(() => {
      const res = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14'];
      const marks: Record<number, string> = {};
      res.forEach((item, index) => {
        if (
          index === 0
          || index === res.length - 1
          || index === searchQuery.value.sectionRange[0]
          || index === searchQuery.value.sectionRange[1]
        ) {
          marks[index] = item;
        }
      });
      return marks;
    });
    /** 搜索结果为空 */
    const isSearchQueryEmpty = computed(() => {
      const sq = searchQuery.value;
      return (
        sq.name.trim() === ''
        && sq.teachers.trim() === ''
        && sq.department.trim() === ''
        && sq.day === '全部'
        && sq.sectionRange[0] === 0
        && sq.sectionRange[1] === 13
        && sq.place.trim() === ''
        && sq.codeId.trim() === ''
      );
    });

    /** 重置 searchQuery */
    const resetSearchQuery = () => {
      searchQuery.value = {
        name: '',
        teachers: '',
        department: '',
        day: '全部',
        sectionRange: [0, 13] as [number, number],
        place: '',
        codeId: '',
      };
    };

    /** 搜索结果 */
    const searchResults = ref<SearchIndexItem[]>([]);
    /** 搜索栏状态 */
    const searchBarStatus = ref<'normal' | 'success' | 'error'>('normal');

    watch(
      searchQuery,
      () => {
        if (searchBarStatus.value !== 'normal') {
          searchBarStatus.value = 'normal';
        }
        if (searchResults.value.length > 0) {
          searchResults.value = [];
        }
      },
      { deep: true },
    );

    /** 搜索结果是否显示 */
    const isSearchResultsVisible = ref(false);

    /** 是否正在搜索 */
    const isLoadingSearchResults = ref(false);

    /** 搜索课程 */
    const search = () => {
      isLoadingSearchResults.value = true;

      // 防止还未渲染 loading 状态就卡住
      setTimeout(() => {
        if (isSearchQueryEmpty.value) {
          isSearchResultsVisible.value = false;
          isLoadingSearchResults.value = false;
          return;
        }

        // 过滤结果
        const sq = searchQuery.value;
        // 对可能出现英文的采用正则匹配以支持大小写，teachers 除外
        const nameReg = new RegExp(sq.name, 'i');
        const placeReg = new RegExp(sq.place, 'i');
        const codeIdReg = new RegExp(sq.codeId, 'i');

        const nameTrimmed = sq.name.trim();
        const teachersTrimmed = sq.teachers.trim();
        const departmentTrimmed = sq.department.trim();
        const placeTrimmed = sq.place.trim();
        const codeIdTrimmed = sq.codeId.trim();
        // const dayRangeStart = sq.dayRange[0] + 1;
        // const dayRangeEnd = sq.dayRange[1] + 1;
        const { day } = sq;
        const sectionRangeStart = sq.sectionRange[0] + 1;
        const sectionRangeEnd = sq.sectionRange[1] + 1;

        searchResults.value = props.searchIndex.filter(
          ({
            name, teachers, department, timeSlots, codeId,
          }) => {
            // 外层每一个 if 中，前面的条件都代表“有有效输入”，即需要根据这个字段进行过滤
            if (nameTrimmed && !nameReg.test(name)) return false;
            if (teachersTrimmed) {
              // 按逗号分隔
              const teachersArray = teachersTrimmed.split(/,|，/);
              // 按与匹配，必须每个老师都在课程教师列表中
              if (teachersArray.filter((t) => !teachers.includes(t)).length > 0) return false;
            }
            if (departmentTrimmed && !department.includes(departmentTrimmed)) return false;
            if (codeIdTrimmed && !codeIdReg.test(codeId)) return false;

            // timeSlots 相关，匹配任意一个即可
            if (
              placeTrimmed
              // dayRangeStart !== 1 ||
              // dayRangeEnd !== 7 ||
              || day !== '全部'
              || sectionRangeStart !== 1
              || sectionRangeEnd !== 14
            ) {
              /* eslint-disable no-continue */
              // eslint-disable-next-line no-restricted-syntax
              for (const ts of timeSlots) {
                if (placeTrimmed && !placeReg.test(ts.place)) continue;
                // if (dayRangeStart > ts.day || dayRangeEnd < ts.day) continue;
                if (day === '星期一' && ts.day !== 1) continue;
                if (day === '星期二' && ts.day !== 2) continue;
                if (day === '星期三' && ts.day !== 3) continue;
                if (day === '星期四' && ts.day !== 4) continue;
                if (day === '星期五' && ts.day !== 5) continue;
                if (day === '星期六' && ts.day !== 6) continue;
                if (day === '星期日' && ts.day !== 7) continue;
                if (sectionRangeStart > ts.section[0] || sectionRangeEnd < ts.section[1]) continue;
                return true;
              }
            } else {
              return true;
            }

            return false;
          },
        );

        if (searchResults.value.length > 0) {
          searchBarStatus.value = 'success';
          message.success(`找到 ${searchResults.value.length} 门课程`, 1);
          // 主要针对移动端，使键盘收回
          // FIXME: 修复下面这部分功能
          // this.$refs.textfield1.blur();
          // this.$refs.textfield2.blur();
          // this.$refs.textfield3.blur();
          // this.$refs.textfield4.blur();
          // this.$refs.textfield5.blur();
        } else {
          searchBarStatus.value = 'error';
          message.error('没有找到符合条件的课程', 1.5);
        }

        isLoadingSearchResults.value = false;
        isSearchResultsVisible.value = true;
      }, 0);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // TODO: 如何在移动端监听键盘“完成”按钮？
      // 监听回车键
      if (e.key === 'Enter') {
        search();
      }
    };

    return {
      searchQuery,
      isSearchResultsVisible,
      searchResults,
      isLoadingSearchResults,
      searchBarStatus,
      sectionLabels,
      isSearchQueryEmpty,
      resetSearchQuery,
      search,
      handleKeyDown,
    };
  },
});
